import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import { LeftSide, RightSide, SectionName, SubHeaderContainer, Tab, TabsDiv, ButtonCreate, RefreshIcon, RefreshIconContainer, OptionsButton } from '../../../components/styles/subheader-style'
import collectionsIcon from '../../../assets/home/shirt.svg'
import familyIcon from '../../../assets/home/family.svg'
import compositionIcon from '../../../assets/home/length.svg'
import { TypeMainOutlet } from '..'
import refresh_act from '../../../assets/refresh-active.svg'
import { Plus, Ellipsis, Tag } from 'lucide-react'
import { Option, OptionsList } from '../Pipelines/PipelineOptionsDropdown/style'
import ViewOrEditThemes from '../../../components/Collections/Modals/ViewOrEditThemes'
import { ViewModeSelector } from '../Pipelines/PipelineControls'

const COMPOSITION_VIEW_MODE_STORAGE_KEY = 'product-development-composition-view-mode'
type CompositionViewMode = 'board' | 'table'

const tabs = [
  { path: '/panel/collection', image: collectionsIcon, name: 'Coleção' },
  { path: '/panel/families', image: familyIcon, name: 'Família' },
  { path: '/panel/composition-product', image: compositionIcon, name: 'Composição de produto' },
]

const ProductDevelopmentContent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const outletContext = useOutletContext<TypeMainOutlet>()
  const [showOptions, setShowOptions] = useState<boolean>(false)
  const optionsRef = useRef<HTMLUListElement | null>(null)
  const optionsButtonRef = useRef<HTMLDivElement | null>(null)
  const [optionsPosition, setOptionsPosition] = useState<{ top: number; right: number } | null>(null)
  const [collections, setCollections] = useState<Array<{ id: number; name: string }>>([])
  const [selectedCollectionForThemes, setSelectedCollectionForThemes] = useState<number | null>(null)
  const [showThemeModal, setShowThemeModal] = useState<boolean>(false)
  const [compositionViewMode, setCompositionViewMode] = useState<CompositionViewMode>(() => {
    const persistedMode = localStorage.getItem(COMPOSITION_VIEW_MODE_STORAGE_KEY)
    return persistedMode === 'board' ? 'board' : 'table'
  })

  const isFormRoute =
    location.pathname.includes('/panel/collection/register') ||
    location.pathname.includes('/panel/collection/edit') ||
    location.pathname.includes('/panel/composition-product/create') ||
    location.pathname.includes('/panel/composition-product/edit')

  const activeTabIndex = (() => {
    if (location.pathname.startsWith('/panel/families')) return 1
    if (location.pathname.startsWith('/panel/composition-product')) return 2
    return 0
  })()

  const activeTabName = activeTabIndex === 0 ? 'collection' : activeTabIndex === 1 ? 'family' : 'composition'

  const isCompositionListRoute =
    location.pathname.startsWith('/panel/composition-product') &&
    !location.pathname.includes('/panel/composition-product/create') &&
    !location.pathname.includes('/panel/composition-product/edit')

  const handleViewThemes = () => {
    if (collections.length === 0) {
      setShowOptions(false)
      return
    }

    if (!selectedCollectionForThemes) {
      setSelectedCollectionForThemes(collections[0].id)
    }

    setShowThemeModal(true)
    setShowOptions(false)
  }

  const toggleOptions = () => {
    setShowOptions((previous) => {
      if (previous) {
        return false
      }

      if (optionsButtonRef.current) {
        const buttonRect = optionsButtonRef.current.getBoundingClientRect()
        const top = buttonRect.bottom + 8
        const right = Math.max(16, window.innerWidth - buttonRect.right)
        setOptionsPosition({ top, right })
      }

      return true
    })
  }

  const handleCompositionViewModeChange = (mode: CompositionViewMode) => {
    setCompositionViewMode(mode)
    localStorage.setItem(COMPOSITION_VIEW_MODE_STORAGE_KEY, mode)
    window.dispatchEvent(new CustomEvent('product-development-view-mode', { detail: { tab: 'composition', viewMode: mode } }))
  }

  const handleRefresh = () => {
    window.dispatchEvent(new CustomEvent('product-development-refresh', { detail: { tab: activeTabName } }))
  }

  const handleCreate = () => {
    if (activeTabName === 'collection') {
      navigate('/panel/collection/register')
      return
    }

    if (activeTabName === 'family') {
      window.dispatchEvent(new CustomEvent('product-development-create', { detail: { tab: activeTabName } }))
      return
    }

    navigate('/panel/composition-product/create')
  }

  const createButtonLabel = activeTabName === 'collection'
    ? 'Nova coleção'
    : activeTabName === 'family'
      ? 'Nova família'
      : 'Novo produto'

  useEffect(() => {
    if (activeTabName !== 'collection') {
      setShowOptions(false)
      return
    }

    const loadCollections = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/collections?limit=100`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        })
        const data = await response.json()
        const list = Array.isArray(data) ? data : data.content || []
        const mappedCollections = list.map((collection: any) => ({
          id: collection.id,
          name: collection.name || `Coleção ${collection.id}`,
        }))

        setCollections(mappedCollections)

        if (mappedCollections.length > 0 && selectedCollectionForThemes === null) {
          setSelectedCollectionForThemes(mappedCollections[0].id)
        }
      } catch (error) {
        console.error('Falha ao carregar coleções para temas:', error)
      }
    }

    loadCollections()
  }, [activeTabName, selectedCollectionForThemes])

  useEffect(() => {
    const handleClickOutsideOptions = (event: MouseEvent) => {
      const target = event.target as Node
      if (optionsRef.current && optionsRef.current.contains(target)) {
        return
      }
      if (optionsButtonRef.current && optionsButtonRef.current.contains(target)) {
        return
      }
      if (showOptions) {
        setShowOptions(false)
      }
    }

    const handleLayoutChange = () => {
      if (showOptions) {
        setShowOptions(false)
      }
    }

    if (showOptions) {
      document.addEventListener('mousedown', handleClickOutsideOptions)
      window.addEventListener('scroll', handleLayoutChange, true)
      window.addEventListener('resize', handleLayoutChange)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideOptions)
      window.removeEventListener('scroll', handleLayoutChange, true)
      window.removeEventListener('resize', handleLayoutChange)
    }
  }, [showOptions])

  useEffect(() => {
    if (!isCompositionListRoute) {
      return
    }

    window.dispatchEvent(new CustomEvent('product-development-view-mode', {
      detail: { tab: 'composition', viewMode: compositionViewMode },
    }))
  }, [isCompositionListRoute, compositionViewMode])

  return (
    <>
      {!isFormRoute && (
        <SubHeaderContainer style={{ zIndex: 20 }}>
          <LeftSide>
            <SectionName>Desenvolvimento de Produto</SectionName>
            <TabsDiv>
              {tabs.map((tab, index) => (
                <Tab
                  key={tab.path}
                  $isFirst={index === 0}
                  $isLast={index === tabs.length - 1}
                  $isActive={index === activeTabIndex}
                  onClick={() => {
                    if (tab.path !== location.pathname) {
                      navigate(tab.path)
                    }
                  }}
                >
                  <img src={tab.image} alt={`${tab.name}-icon`} />
                  {tab.name}
                </Tab>
              ))}
            </TabsDiv>
          </LeftSide>
          <RightSide>
            {activeTabName === 'collection' && (
              <>
                <OptionsButton ref={optionsButtonRef} onClick={toggleOptions} style={{ position: 'relative' }}>
                  <Ellipsis size={18} />
                </OptionsButton>
                {showOptions && optionsPosition && ReactDOM.createPortal(
                  <OptionsList ref={optionsRef} style={{ top: `${optionsPosition.top}px`, right: `${optionsPosition.right}px`, left: 'auto', position: 'fixed', zIndex: 2147483647, minWidth: 'unset', width: 'max-content' }}>
                    <Option onClick={() => { handleViewThemes(); setShowOptions(false) }}>
                      <Tag size={18} /> Temas
                    </Option>
                  </OptionsList>,
                  document.body,
                )}
              </>
            )}
            {activeTabName === 'composition' && isCompositionListRoute && (
              <ViewModeSelector
                value={compositionViewMode}
                onChange={(mode) => handleCompositionViewModeChange(mode as CompositionViewMode)}
                boardLabel='Quadro'
                tableLabel='Tabela'
              />
            )}
            <RefreshIconContainer onClick={handleRefresh} title='Sincronizar'>
              <RefreshIcon src={refresh_act} alt='Sincronizar' />
            </RefreshIconContainer>
            <ButtonCreate onClick={handleCreate}>
              <Plus size={18} />
              {createButtonLabel}
            </ButtonCreate>
          </RightSide>
        </SubHeaderContainer>
      )}
      <Outlet context={outletContext} />
      {showThemeModal && selectedCollectionForThemes && (
        <ViewOrEditThemes
          collectionId={selectedCollectionForThemes}
          closeModal={() => setShowThemeModal(false)}
          onSave={() => {
            setShowThemeModal(false)
          }}
        />
      )}
    </>
  )
}

export default ProductDevelopmentContent
